/**
 * SeriesPage - Displays all Pokemon card series in a grid
 * Shows series cards with navigation to individual series
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PageMeta from '../components/PageMeta';

const AllSeriesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // State
  const [seriesData, setSeriesData] = useState([]);
  const [setsData, setSetsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('newest');

  const apiUrl = process.env.REACT_APP_API_URL || '';

  // Fetch series and sets data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch series data
        const seriesResponse = await fetch(`${apiUrl}/api/series/`);
        if (!seriesResponse.ok) {
          throw new Error('Network response was not ok for series data');
        }
        const seriesData = await seriesResponse.json();
        setSeriesData(seriesData);
        
        // Fetch sets data
        const setsResponse = await fetch(`${apiUrl}/api/set/`);
        if (!setsResponse.ok) {
          throw new Error('Network response was not ok for sets data');
        }
        const setsData = await setsResponse.json();
        setSetsData(setsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [apiUrl]);

  const normalizeQuery = (value) => value.toLowerCase().trim();

  const formatDate = (value) => {
    if (!value) return 'Unknown';
    const normalized = String(value).replace(/\//g, '-');
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) return 'Unknown';
    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const doesSetMatchQuery = (set, query) => {
    if (!query) return false;
    const haystack = [
      set.name,
      set.id,
      set.ptcgoCode,
      set.series,
      set.series_id,
    ]
      .filter(Boolean)
      .map(normalizeQuery)
      .join(' ');
    return haystack.includes(query);
  };

  const getFilteredSets = () => {
    const query = normalizeQuery(searchQuery);
    if (!query) return [];
    return setsData.filter((set) => doesSetMatchQuery(set, query));
  };

  // Filter and sort series
  const getFilteredAndSortedSeries = () => {
    const query = normalizeQuery(searchQuery);

    // Filter by search query (series name or matching sets)
    let filtered = seriesData;
    if (query) {
      const matchedSeriesIds = new Set();
      const matchedSeriesNames = new Set();
      setsData.forEach((set) => {
        if (doesSetMatchQuery(set, query)) {
          if (set.series_id) matchedSeriesIds.add(set.series_id);
          if (set.series) matchedSeriesNames.add(set.series.toLowerCase());
        }
      });

      filtered = seriesData.filter((series) => {
        const seriesName = normalizeQuery(series.name || '');
        return seriesName.includes(query) ||
          matchedSeriesIds.has(series.id) ||
          matchedSeriesNames.has(seriesName);
      });
    }
    
    // Sort based on selected option
    switch(sortOption) {
      case 'newest':
        return [...filtered].sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate));
      case 'oldest':
        return [...filtered].sort((a, b) => new Date(a.releaseDate) - new Date(b.releaseDate));
      case 'name-asc':
        return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
      case 'name-desc':
        return [...filtered].sort((a, b) => b.name.localeCompare(a.name));
      case 'cards-high':
        return [...filtered].sort((a, b) => b.total - a.total);
      case 'cards-low':
        return [...filtered].sort((a, b) => a.total - b.total);
      default:
        return filtered;
    }
  };

  // Get sets belonging to a specific series
  const getSeriesSets = (seriesId) => {
    return setsData.filter(set => set.series === seriesId || set.series === seriesData.find(s => s.id === seriesId)?.name);
  };

  // Render series card
  const renderSeriesCard = (series) => {
    // Format the release date
    const formattedDate = formatDate(series.releaseDate);

    // Get sets for this series
    const seriesSets = series.sets ? 
      setsData.filter(set => series.sets.includes(set.id)) : 
      getSeriesSets(series.id);
    
    // Sort sets by release date
    seriesSets.sort((a, b) => new Date(a.releaseDate) - new Date(b.releaseDate));

    return (
      <div 
        key={series.id} 
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/series/${series.id}`)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            navigate(`/series/${series.id}`);
          }
        }}
        className="bg-dark border border-purple/20 rounded-lg overflow-hidden hover:border-purple/60 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
      >        <div className="relative">
          <img 
            src={series.LogoUrl} 
            alt={series.name} 
            className="w-full h-48 object-contain"
            onError={(e) => {
              if (series.backupLogoUrl) {
                e.target.src = series.backupLogoUrl;
              } else {
                e.target.src = '/placeholder-card.svg';
              }
            }}
          />
          <div className="absolute top-2 right-2 bg-dark/80 text-xs font-medium text-skyblue px-2 py-1 rounded">
            Pokémon TCG
          </div>
        </div>
        <div className="p-4">
          <h3 className="text-lg font-medium text-cream mb-1">{series.name} Series</h3>
          <div className="flex items-center text-xs text-cream/60 mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            {formattedDate}
          </div>
          <p className="text-sm text-cream/80 mb-2 line-clamp-2">
            Contains {seriesSets.length} sets with a total of {series.total} cards.
          </p>
          
          {/* Set links section */}
          <div className="mb-3 overflow-hidden">
            <div className="flex flex-wrap gap-1 text-xs text-cream/40">
              {seriesSets.slice(0, 6).map((set, index) => (
                <span 
                  key={set.id} 
                  className="hover:text-skyblue transition-colors cursor-pointer px-2 py-1 rounded-full bg-purple/10 text-cream/50 hover:text-skyblue inline-block mb-1 mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/sets/${set.id}`);
                  }}
                >
                  {set.name}{index < Math.min(seriesSets.length, 6) - 1 ? '' : ''}
                  {index === 5 && seriesSets.length > 6 ? '' : ''}
                </span>
              ))}
            </div>
          </div>
          
          <div className="flex justify-between items-center">
            <div className="flex items-center text-xs text-green">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path>
              </svg>
              {series.total} Cards
            </div>
            <span className="text-xs text-skyblue hover:text-cream transition-colors">View Series →</span>
          </div>
        </div>
      </div>
    );
  };

  const renderSetCard = (set) => {
    const totalCards = set.total || set.printed_total || set.printedTotal || 0;
    const formattedDate = formatDate(set.releaseDate);
    return (
      <div
        key={set.id}
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/sets/${set.id}`)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            navigate(`/sets/${set.id}`);
          }
        }}
        className="bg-dark border border-purple/20 rounded-lg overflow-hidden hover:border-purple/60 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
      >
        <div className="relative">
          <img
            src={set.LogoUrl}
            alt={set.name}
            className="w-full h-40 object-contain"
            onError={(e) => {
              if (set.backupLogoUrl) {
                e.target.src = set.backupLogoUrl;
              } else {
                e.target.src = '/placeholder-card.svg';
              }
            }}
          />
          {set.ptcgoCode ? (
            <div className="absolute top-2 right-2 bg-dark/80 text-xs font-medium text-skyblue px-2 py-1 rounded">
              {set.ptcgoCode}
            </div>
          ) : null}
        </div>
        <div className="p-4">
          <h3 className="text-lg font-medium text-cream mb-1">{set.name}</h3>
          <div className="flex items-center text-xs text-cream/60 mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            {formattedDate}
          </div>
          <p className="text-sm text-cream/80 mb-2 line-clamp-2">
            {set.series} • {totalCards} cards
          </p>
          <div className="flex justify-between items-center">
            <span className="text-xs text-green">{totalCards} Cards</span>
            <span className="text-xs text-skyblue hover:text-cream transition-colors">View Set →</span>
          </div>
        </div>
      </div>
    );
  };

  // Render loading state
  const renderLoading = () => (
    <div className="col-span-full flex justify-center p-8">
      <div className="animate-pulse flex flex-col items-center">
        <div className="h-8 w-32 bg-purple/20 rounded mb-2"></div>
        <div className="h-4 w-48 bg-purple/10 rounded"></div>
      </div>
    </div>
  );

  // Render empty state
  const renderEmpty = () => (
    <div className="col-span-full text-center p-8">
      <p className="text-cream/70">No series found matching your search criteria.</p>
    </div>
  );

  // Render series grid
  const renderSeriesGrid = () => {
    const filteredSeries = getFilteredAndSortedSeries();
    const matchedSets = getFilteredSets();

    if (filteredSeries.length === 0 && matchedSets.length === 0) {
      return renderEmpty();
    }

    return (
      <div className="grid gap-8">
        {matchedSets.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-cream">Matching Sets</h2>
              <span className="text-xs text-cream/60">{matchedSets.length} results</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {matchedSets.map((set) => renderSetCard(set))}
            </div>
          </div>
        )}

        {filteredSeries.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-cream">Matching Series</h2>
              <span className="text-xs text-cream/60">{filteredSeries.length} results</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSeries.map((series) => renderSeriesCard(series))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <PageMeta
        title={
          location.pathname === '/'
            ? 'Lilycove - Pokemon Card Tracker with Live Prices'
            : 'Pokemon Series | Lilycove'
        }
        description="Browse all Pokémon TCG series on Lilycove."
      />
      {/* Search Bar (Mobile) */}
      <div className="relative mb-8 md:hidden">
        <input 
          type="text" 
          placeholder="Search series or sets..." 
          className="w-full h-9 rounded-md border border-purple/30 bg-dark/80 px-3 py-1 pl-9 text-cream placeholder:text-cream/50"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-2.5 h-5 w-5 text-purple">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.3-4.3"></path>
        </svg>
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center text-sm mb-6 text-cream/70">
        <Link to="/" className="text-skyblue hover:text-cream">Home</Link>
        <span className="mx-2">/</span>
      </div>

      {/* Series Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl tracking-wider uppercase mb-1 font-medium text-purple">Pokémon Card Series</h1>
          <p className="text-cream/70 text-sm tracking-wide">Browse all Pokémon TCG series</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div className="relative w-full sm:w-64">
            <input 
              type="text" 
              id="series-search"
              placeholder="Search series or sets" 
              className="w-full h-9 rounded-md border border-purple/30 bg-dark/80 px-3 py-1 pl-9 text-cream placeholder:text-cream/50"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-2.5 h-4 w-4 text-purple/70">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
          </div>

          <div className="flex gap-3">
            <div className="relative inline-block w-[140px]">
              <select 
                id="sort-select" 
                className="w-full h-9 rounded-md border border-purple/30 bg-dark px-3 py-1 text-cream appearance-none pr-8"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name-asc">Name: A-Z</option>
                <option value="name-desc">Name: Z-A</option>
                <option value="cards-high">Most Cards</option>
                <option value="cards-low">Fewest Cards</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cream">
                  <path d="m6 9 6 6 6-6"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Grid with Sidebar */}
      <div className="grid gap-6">
        {/* Series Grid */}
        <div className="lg:col-span-3">
          {isLoading ? renderLoading() : renderSeriesGrid()}
        </div>
      </div>
    </>
  );
};

export default AllSeriesPage;
